import { BottomSheet, Text, type UseModalState } from "@clipboard-health/ui-components";
import { Box, Fade, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import { useCallback, useEffect, useRef, useState } from "react";

import act1AnimationJson from "./animations/act1.json";
import act2AnimationJson from "./animations/act2.json";
import act3AnimationJson from "./animations/act3.json";
import { useShouldShowFortuneCookie } from "./hooks/useShouldShowFortuneCookie";

const FORTUNE_COOKIE_TAP_COUNT = 6;

type AnimationState = "idle" | "clicking" | "opened";

type FortuneAnimationData = {
  layers: unknown;
} & Record<string, unknown>;

function isFortuneAnimationData(value: unknown): value is FortuneAnimationData {
  return typeof value === "object" && value !== null && "layers" in value;
}

function getAnimationData(animation: unknown, animationName: AnimationState): FortuneAnimationData {
  if (!isFortuneAnimationData(animation)) {
    throw new Error(`Invalid ${animationName} fortune animation data`);
  }

  return animation;
}

const animationByState: Record<AnimationState, FortuneAnimationData> = {
  idle: getAnimationData(act1AnimationJson, "idle"),
  clicking: getAnimationData(act2AnimationJson, "clicking"),
  opened: getAnimationData(act3AnimationJson, "opened"),
};

interface FortuneCookieBottomSheetProps {
  modalState: UseModalState;
  fortuneMessage?: string;
}

export function FortuneCookieBottomSheet(props: FortuneCookieBottomSheetProps) {
  const { modalState, fortuneMessage: propertyFortuneMessage } = props;
  const { fortuneMessage: hookFortuneMessage, hide, recordShown } = useShouldShowFortuneCookie();

  // Use prop message if provided, otherwise use hook message
  const selectedFortune = propertyFortuneMessage ?? hookFortuneMessage;

  const [animationState, setAnimationState] = useState<AnimationState>("idle");
  const [tapCount, setTapCount] = useState(0);
  const [showFortune, setShowFortune] = useState(false);
  const [scale, setScale] = useState(1);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  // Reset state and hide fortune cookie when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setAnimationState("idle");
      setTapCount(0);
      setShowFortune(false);
      setScale(1);
      // Always hide the fortune cookie when modal closes, regardless of whether fortune was shown
      hide();
    }
  }, [modalState.modalIsOpen, hide]);

  // Log event and record next show time when fortune is displayed
  useEffect(() => {
    if (showFortune) {
      logEvent(APP_V2_APP_EVENTS.FORTUNE_VIEWED, {
        fortune_message: selectedFortune,
        timestamp: new Date().toISOString(),
      });
      // Record that fortune was shown and set next show time (8-24 hours from now)
      recordShown();
    }
  }, [showFortune, selectedFortune, recordShown]);

  const handleTap = useCallback(() => {
    if (animationState === "opened") {
      return;
    }

    // Visual feedback: scale animation
    setScale(0.9);
    setTimeout(() => {
      setScale(1);
    }, 100);

    if (animationState === "idle") {
      setAnimationState("clicking");
      setTapCount(1);
      return;
    }

    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    if (newTapCount >= FORTUNE_COOKIE_TAP_COUNT) {
      setAnimationState("opened");
      // Show fortune text after animation completes
      setTimeout(() => {
        setShowFortune(true);
      }, 1900);
    }
  }, [animationState, tapCount]);

  function getCurrentAnimation(): FortuneAnimationData {
    return animationByState[animationState];
  }

  function shouldLoop() {
    return animationState !== "opened";
  }

  function getInstructionText(): string {
    if (tapCount === 0) {
      return "Tap to reveal your fortune";
    }

    if (tapCount < 3) {
      return "Keep going!";
    }

    return "Almost there!";
  }

  if (!selectedFortune) {
    return null;
  }

  return (
    <BottomSheet
      canDismiss
      modalState={modalState}
      paperSx={(theme) => ({
        background: theme.palette.instantPay?.background,
        // height: "70vh",
      })}
    >
      <Box
        sx={{
          cursor: animationState === "opened" ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: 5,
        }}
        onClick={handleTap}
      >
        <Stack spacing={3} alignItems="center" sx={{ width: "100%", maxWidth: "400px" }}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "1",
              transform: `scale(${scale})`,
              transition: "transform 0.1s ease-out",
              isolation: "isolate",
            }}
          >
            <Lottie
              lottieRef={lottieRef}
              animationData={getCurrentAnimation()}
              loop={shouldLoop()}
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                zIndex: 1,
              }}
            />

            {/* Prominent instruction text overlay */}
            {animationState !== "opened" && (
              <Box
                sx={{
                  position: "absolute",
                  top: "65%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  textAlign: "center",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                <Text
                  bold
                  variant="h2"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                  })}
                >
                  {getInstructionText()}
                </Text>
              </Box>
            )}

            {/* Fortune text overlay */}
            <Fade in={showFortune}>
              <Box
                sx={{
                  position: "absolute",
                  top: "75%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "70%",
                  textAlign: "center",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <Text bold variant="h5">
                  {selectedFortune}
                </Text>
              </Box>
            </Fade>
          </Box>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
